<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <template v-if="currScheme">
      <div class="scheme-detail-shell sba-slide-in">
      <div class="report-section scheme-detail-header">
        <div class="detail-heading">
          <i class="material-icons">local_hospital</i>
          <div>
            <h6 class="m-0">{{ currScheme.name }}</h6>
            <small class="grey-text">Scheme | {{ currScheme.year }}</small>
          </div>
          <div class="detail-actions">
            <button class="btn-flat cancel-detail-button" @click.prevent="handleBack">
              <i class="material-icons left">close</i>Cancel
            </button>
          </div>
        </div>

        <div class="scheme-overview-actions">
          <button class="btn-small waves-effect waves-light cyan darken-3" @click.prevent="downloadScheme">
            <i class="material-icons left">file_download</i>Download
          </button>
        </div>

        <div class="metric-grid">
          <div class="metric-block">
            <h6>{{ currScheme.enrollees_count }}</h6>
            <small>Active enrollees</small>
          </div>
          <div class="metric-block">
            <h6>{{ currScheme.encounters_count }}</h6>
            <small>Encounters</small>
          </div>
          <div class="metric-block">
            <h6>{{ formatMoney(currScheme.gross_claims ?? currScheme.claims) }}</h6>
            <small>Gross Utilization</small>
            <small class="real-utilization-line">{{ formatMoney(currScheme.real_claims) }} real</small>
          </div>
        </div>
      </div>

          <div class="card-panel z-depth-0 report-panel">
            <h6>Monthly Utilization</h6>
            <div class="month-row" v-for="month in currScheme.monthly_utilization" :key="month.month">
              <span>{{ month.month }}</span>
              <div class="month-bar">
                <span :style="`width: ${monthlyWidth(month.sent)}%`"></span>
              </div>
              <div class="right-align">
                <strong>{{ formatMoney(month.gross_sent ?? month.sent) }}</strong><br />
                <small>{{ formatMoney(month.real_approved) }} real</small>
              </div>
            </div>
            <p class="grey-text" v-if="!currScheme.monthly_utilization.length">No encounters for this year.</p>
          </div>

          <div class="card-panel z-depth-0 report-panel">
            <h6>Provider Utilization</h6>
            <div class="provider-row" v-for="provider in currScheme.provider_utilization" :key="provider.provider_id">
              <div>
                <strong>{{ provider.provider?.name || "Provider" }}</strong><br />
                <small>{{ provider.encounter_count }} encounters</small>
              </div>
              <div class="right-align">
                <strong>{{ formatMoney(provider.gross_sent ?? provider.sent) }}</strong><br />
                <small>{{ formatMoney(provider.real_approved) }} real</small>
              </div>
            </div>
            <p class="grey-text" v-if="!currScheme.provider_utilization.length">No provider utilization for this year.</p>
          </div>

      <div class="card-panel z-depth-0 report-panel">
        <h6>Encounters</h6>
        <div class="scheme-filter-grid encounter-filter-grid">
          <div>
            <div class="input-field encounter-search">
              <label class="active">Search</label>
              <input
                type="search"
                v-model="encounterSearch"
                placeholder="Search enrollee, provider, diagnosis or status"
                @keyup.enter="handleEncounterSearch"
                @search="handleEncounterSearch"
              />
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="encounterProvider" @change="handleEncounterSearch">
                <option value="">All providers</option>
                <option :value="provider.provider_id" v-for="provider in currScheme.provider_utilization" :key="provider.provider_id">
                  {{ provider.provider?.name || "Provider" }}
                </option>
              </select>
              <label>Provider</label>
            </div>
          </div>
          <div>
            <div class="input-field">
              <select v-model="encounterMonth" @change="handleEncounterSearch">
                <option value="">All months</option>
                <option :value="month.month" v-for="month in currScheme.monthly_utilization" :key="month.month">
                  {{ month.month }}
                </option>
              </select>
              <label>Month</label>
            </div>
          </div>
          <div class="encounter-actions">
            <button class="btn-small waves-effect waves-light grey" :class="{ disabled: !encounterSearch && !encounterProvider && !encounterMonth }" @click.prevent="clearEncounterFilters">
              <i class="material-icons">clear</i>
            </button>
          </div>
        </div>
        <div class="collection">
          <div class="collection-item" v-for="encounter in pagedEncounters" :key="encounter.id">
            <div class="row m-0">
              <div class="col s12 m7">
                <strong>{{ encounter.name }}</strong><br />
                <small>{{ encounter.provider?.name || "Provider" }} | {{ encounter.diagnosis || "No diagnosis" }}</small>
              </div>
              <div class="col s12 m5 right-align">
                <strong>{{ formatMoney(encounter.gross_sent ?? encounter.sent) }}</strong><br />
                <small>{{ formatShortDate(encounter.encounter_date) }} | {{ encounter.status }}</small>
              </div>
            </div>
          </div>
        </div>
        <div class="encounter-pager" v-if="encounterPageTotal > 1">
          <button
            class="btn-flat encounter-page-button"
            :class="{ disabled: encounterPage <= 1 }"
            @click.prevent="changeEncounterPage(encounterPage - 1)"
          >
            <i class="material-icons left">chevron_left</i>Previous
          </button>
          <span>Page {{ encounterPage }} of {{ encounterPageTotal }}</span>
          <button
            class="btn-flat encounter-page-button"
            :class="{ disabled: encounterPage >= encounterPageTotal }"
            @click.prevent="changeEncounterPage(encounterPage + 1)"
          >
            Next<i class="material-icons right">chevron_right</i>
          </button>
        </div>
        <p class="grey-text" v-if="!pagedEncounters.length">No encounters match this filter.</p>
      </div>
      </div>
    </template>

    <template v-else>
      <div class="schemes-insight-shell sba-slide-in">
      <div class="schemes-insight-head">
        <div>
          <label class="active teal-text">Schemes Insight</label>
          <h5>Scheme Utilization Review</h5>
          <p>
            Review scheme enrollee volume, encounter activity, provider utilization, and year-to-date claims movement.
          </p>
        </div>
        <span class="scope-chip">
          Schemes
          <em>{{ selectedYear }}</em>
        </span>
      </div>

      <div class="filter-panel">
        <div class="filter-title">
          <div>
            <label class="active teal-text">Scope</label>
            <h6>Scheme Filters</h6>
          </div>
          <i class="material-icons">tune</i>
        </div>
        <div class="filter-grid">
          <div>
          <div class="input-field">
            <input type="search" v-model="searchItem" @keyup.enter="handleSearch" placeholder="Search scheme" />
          </div>
        </div>
          <div>
          <div class="input-field">
            <select v-model="selectedYear" @change="handleSearch">
              <option :value="year" v-for="year in years" :key="year">{{ year }}</option>
            </select>
            <label>Year</label>
          </div>
        </div>
          <div class="report-actions">
          <button class="btn-small waves-effect waves-light grey action-button" :class="{ disabled: !searchItem }" @click.prevent="handleClear">
            <i class="material-icons left">clear</i>Clear
          </button>
          <button class="btn-small waves-effect waves-light cyan darken-3 action-button" @click.prevent="handleSearch">
            <i class="material-icons left">search</i>Search
          </button>
          <button class="btn-small waves-effect waves-light cyan darken-3 action-button" :class="{ disabled: !schemes.length }" @click.prevent="downloadSchemes">
            <i class="material-icons left">file_download</i>Download
          </button>
        </div>
        </div>
      </div>

      <div class="secondary-panel" v-if="summary">
        <div class="summary-grid">
          <div class="summary-tile schemes">
            <small>Schemes</small>
            <strong>{{ summary.schemes_count }}</strong>
          </div>
          <div class="summary-tile enrollees">
            <small>Active Enrollees</small>
            <strong>{{ summary.enrollees_count }}</strong>
          </div>
          <div class="summary-tile encounters">
            <small>Encounters</small>
            <strong>{{ summary.encounters_count }}</strong>
          </div>
          <div class="summary-tile claims">
            <small>Gross Utilization</small>
            <strong>{{ formatMoney(summary.gross_claims ?? summary.claims) }}</strong>
            <small>{{ formatMoney(summary.real_claims) }} real</small>
          </div>
        </div>
      </div>

        <div
          class="scheme-list-item clickable"
          v-for="scheme in schemes"
          :key="scheme.id"
          @click.prevent="handleDetail(scheme)"
        >
          <div class="report-section scheme-card">
            <div class="scheme-row">
              <i class="material-icons">local_hospital</i>
              <div class="scheme-main">
                <strong>{{ scheme.name }}</strong>
                <small>Scheme | {{ scheme.year }}</small>
              </div>
              <div class="metric-block">
                <small>Active enrollees</small>
                <strong>{{ scheme.enrollees_count }}</strong>
              </div>
              <div class="metric-block">
                <small>Encounters</small>
                <strong>{{ scheme.encounters_count }}</strong>
              </div>
              <div class="metric-block">
                <small>Gross Utilization</small>
                <strong>{{ formatMoney(scheme.gross_claims ?? scheme.claims) }}</strong>
                <small>{{ formatMoney(scheme.real_claims) }} real</small>
              </div>
              <div class="client-action">View</div>
            </div>
            <div class="measure-track" :title="`${schemeWidth(scheme.claims)}% of highest scheme utilization in this scope`">
              <span :style="`width: ${schemeWidth(scheme.claims)}%`"></span>
            </div>
            <small class="grey-text">Utilization compared with the highest scheme in this scope.</small>
          </div>
        </div>

      <div class="section center" v-if="!schemes.length">
        <h6>No Scheme Report <em>found</em>...</h6>
      </div>
      </div>
    </template>
  </template>
</template>

<script>
import { onMounted, onUpdated, ref, computed, nextTick } from "vue";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import nativeUi from "@/ui/native-ui";

export default {
  name: "SchemesInsightComponent",
  components: {
    LoadingDiv,
  },
  setup() {
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currScheme = ref(null);
    const selectedYear = ref(new Date().getFullYear());
    const searchItem = ref("");
    const currUrl = ref("operator/insight/schemes");
    const enrolleeSearch = ref("");
    const enrolleeType = ref("all");
    const encounterSearch = ref("");
    const encounterProvider = ref("");
    const encounterMonth = ref("");
    const encounterPage = ref(1);
    const encounterPageSize = 10;
    const enrolleeTypes = ref([
      { value: "all", label: "All" },
      { value: "p", label: "Principals" },
      { value: "s", label: "Spouses" },
      { value: "d", label: "Dependants" },
      { value: "u", label: "Other" },
    ]);

    const initSelect = () => {
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    };

    onMounted(initSelect);
    onUpdated(initSelect);

    const years = computed(() => {
      const year = new Date().getFullYear();
      return [year, year - 1, year - 2, year - 3, year - 4];
    });

    const formatMoney = (val) => {
      return `NGN ${Number(val || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const formatShortDate = (val) => {
      if (!val) return "";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const labelType = (type) => {
      if (type === "p") return "Principal";
      if (type === "s") return "Spouse";
      if (type === "d") return "Dependant";
      return "Enrollee";
    };

    const buildUrl = () => {
      const params = [`year=${selectedYear.value}`];

      if (searchItem.value) {
        params.push(`q=${encodeURIComponent(searchItem.value)}`);
      }

      return `${currUrl.value}?${params.join("&")}`;
    };

    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
      })();
    };

    const buildSchemeDetailUrl = (scheme, page = encounterPage.value) => {
      const params = [`year=${selectedYear.value}`];

      params.push(`encounter_page=${page}`);
      params.push(`encounter_per_page=${encounterPageSize}`);

      if (encounterSearch.value) {
        params.push(`encounter_q=${encodeURIComponent(encounterSearch.value)}`);
      }

      if (encounterProvider.value) {
        params.push(`encounter_provider=${encounterProvider.value}`);
      }

      if (encounterMonth.value) {
        params.push(`encounter_month=${encounterMonth.value}`);
      }

      return `operator/insight/scheme/${scheme.id}?${params.join("&")}`;
    };

    const getSchemeFromApi = (scheme, page = encounterPage.value, scrollToTop = false) => {
      (async () => {
        isReady.value = false;
        const { res, err } = await getItems(buildSchemeDetailUrl(scheme, page));

        if (res.value) {
          currScheme.value = res.value.data;
          encounterPage.value = res.value.data.encounters?.current_page || 1;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
        if (res.value && scrollToTop) {
          await nextTick();
          window.scrollTo({
            top: 0,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          });
        }
      })();
    };

    onMounted(() => {
      getItemsFromApi(buildUrl());
    });

    const handleSearch = () => {
      currScheme.value = null;
      isReady.value = false;
      getItemsFromApi(buildUrl());
    };

    const handleClear = () => {
      searchItem.value = "";
      handleSearch();
    };

    const handleDetail = (scheme) => {
      enrolleeSearch.value = "";
      enrolleeType.value = "all";
      encounterPage.value = 1;
      clearEncounterFilters();
      getSchemeFromApi(scheme, 1, true);
    };

    const handleBack = () => {
      currScheme.value = null;
    };

    const clearEncounterFilters = () => {
      encounterSearch.value = "";
      encounterProvider.value = "";
      encounterMonth.value = "";
      encounterPage.value = 1;

      if (currScheme.value) {
        getSchemeFromApi(currScheme.value, 1);
      }
    };

    const handleEncounterSearch = () => {
      encounterPage.value = 1;
      getSchemeFromApi(currScheme.value, 1);
    };

    const changeEncounterPage = (page) => {
      if (page < 1 || page > encounterPageTotal.value) return;
      encounterPage.value = page;
      getSchemeFromApi(currScheme.value, page);
    };

    const monthlyWidth = (sent) => {
      const max = Math.max(...currScheme.value.monthly_utilization.map((x) => Number(x.sent || 0)), 0);
      if (!max) return 0;
      return Math.round((Number(sent || 0) / max) * 100);
    };

    const schemeWidth = (claims) => {
      const max = Math.max(...schemes.value.map((scheme) => Number(scheme.claims || 0)), 0);
      if (!max) return 0;
      return Math.round((Number(claims || 0) / max) * 100);
    };

    const downloadSchemes = () => {
      const data = schemes.value.map((scheme, index) => ({
        No: index + 1,
        Scheme: scheme.name,
        Year: scheme.year,
        ActiveEnrollees: scheme.enrollees_count,
        Encounters: scheme.encounters_count,
        "Gross Utilization": scheme.gross_claims ?? scheme.claims,
        "Real Utilization": scheme.real_claims,
      }));

      if (data.length) {
        downloadExcelFx(data, `Scheme_Utilization_${selectedYear.value}`);
      }
    };

    const downloadScheme = () => {
      const data = [];

      currScheme.value.provider_utilization.forEach((provider, index) => {
        data.push({
          No: index + 1,
          Section: "Provider utilization",
          Scheme: currScheme.value.name,
          Year: currScheme.value.year,
          Provider: provider.provider?.name || "Provider",
          Encounters: provider.encounter_count,
          "Gross Utilization": provider.gross_sent ?? provider.sent,
          "Real Utilization": provider.real_approved,
        });
      });

      data.push({});
      data.push({
        No: "",
        Section: "Summary",
        Scheme: currScheme.value.name,
        Year: currScheme.value.year,
        Provider: "Total",
        Encounters: currScheme.value.encounters_count,
        "Gross Utilization": currScheme.value.gross_claims ?? currScheme.value.claims,
        "Real Utilization": currScheme.value.real_claims,
      });

      data.push({});

      filteredEnrollees.value.forEach((enrollee, index) => {
        data.push({
          No: index + 1,
          Section: "Enrollee list",
          Scheme: currScheme.value.name,
          Year: currScheme.value.year,
          Provider: enrollee.picked_provider || "",
          EnrolleeType: labelType(enrollee.type),
          EnrolleeNumber: enrollee.enrollee_number || "",
          Name: enrollee.name,
          Plan: enrollee.plan?.name || "",
          Period: `${enrollee.new_start_date} - ${enrollee.new_end_date}`,
        });
      });

      data.push({});

      pagedEncounters.value.forEach((encounter, index) => {
        data.push({
          No: index + 1,
          Section: "Encounters",
          Scheme: currScheme.value.name,
          Year: currScheme.value.year,
          Provider: encounter.provider?.name || "Provider",
          EncounterDate: encounter.encounter_date,
          "Gross Utilization": encounter.gross_sent ?? encounter.sent,
          "Real Utilization": encounter.real_approved,
          Name: encounter.name,
          EnrolleeNumber: encounter.enrollee_number || "",
          EnrolleeType: labelType(encounter.type),
          Plan: encounter.plan?.name || "",
          Diagnosis: encounter.diagnosis || "",
          Status: encounter.status,
        });
      });

      if (data.length) {
        downloadExcelFx(data, `${currScheme.value.name}_Utilization_${currScheme.value.year}`);
      }
    };

    const schemes = computed(() => response.value?.items || []);
    const summary = computed(() => response.value?.summary || null);
    const filteredEnrollees = computed(() => {
      const q = enrolleeSearch.value?.toLowerCase();

      return (currScheme.value?.enrollees || []).filter((enrollee) => {
        const haystack = [
          enrollee.name,
          enrollee.enrollee_number,
          enrollee.type,
          labelType(enrollee.type),
          enrollee.plan?.name,
        ].join(" ").toLowerCase();

        const typePass = enrolleeType.value === "all" || enrollee.type === enrolleeType.value;
        const searchPass = !q || haystack.includes(q);

        return typePass && searchPass;
      });
    });

    const pagedEncounters = computed(() => {
      return currScheme.value?.encounters?.data || [];
    });

    const encounterPageTotal = computed(() => {
      return currScheme.value?.encounters?.last_page || 1;
    });

    return {
      handleSearch,
      handleClear,
      handleDetail,
      handleBack,
      clearEncounterFilters,
      handleEncounterSearch,
      changeEncounterPage,
      downloadSchemes,
      downloadScheme,
      monthlyWidth,
      schemeWidth,
      formatMoney,
      formatShortDate,
      labelType,
      isReady,
      error,
      currScheme,
      selectedYear,
      searchItem,
      enrolleeSearch,
      enrolleeType,
      enrolleeTypes,
      encounterSearch,
      encounterProvider,
      encounterMonth,
      encounterPage,
      encounterPageTotal,
      years,
      schemes,
      summary,
      filteredEnrollees,
      pagedEncounters,
    };
  },
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.m-0 {
  margin: 0;
}

.schemes-insight-shell {
  color: #263238;
  display: grid;
  gap: 16px;
  margin-top: 8px;
}

.schemes-insight-head,
.filter-panel,
.secondary-panel,
.report-section,
.report-panel,
.highlight-panel {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: none;
}

.schemes-insight-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 136px;
  padding: 28px;
}

.schemes-insight-head h5 {
  color: #263238;
  font-size: 1.24rem;
  font-weight: 650;
  margin: 4px 0 0;
}

.schemes-insight-head p {
  color: #607d8b;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 760px;
}

.scope-chip {
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  box-shadow: none;
  color: #455a64;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.3;
  padding: 6px 10px;
  text-align: right;
}

.scope-chip em {
  color: #455a64;
  font-size: 0.76rem;
  font-style: normal;
}

.scheme-detail-shell {
  color: #263238;
  display: grid;
  gap: 16px;
  margin-top: 0;
}

.scheme-detail-shell > .row {
  margin-bottom: 0;
}

.scheme-detail-shell > .card-panel,
.scheme-detail-shell > .report-section {
  margin: 0;
  width: 100%;
}

.filter-panel {
  background: #f7fafb;
  margin: 0;
  padding: 24px;
}

.filter-title {
  align-items: flex-start;
  border-bottom: 1px solid #e7e7e7;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  padding-bottom: 16px;
}

.filter-title h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.filter-title i {
  color: #90a4ae;
  font-size: 24px;
}

.filter-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(220px, 1fr) minmax(180px, 0.5fr) minmax(280px, auto);
  align-items: end;
}

.filter-grid .input-field {
  margin: 0;
}

.secondary-panel {
  margin-bottom: 0;
  padding: 24px;
}

.summary-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.summary-tile {
  border-radius: 6px;
  color: #ffffff;
  min-height: 88px;
  padding: 16px;
}

.summary-tile.schemes {
  background: #1565c0;
}

.summary-tile.enrollees {
  background: #00897b;
}

.summary-tile.encounters {
  background: #ef6c00;
}

.summary-tile.claims {
  background: #6a1b9a;
}

.summary-tile small,
.summary-tile strong {
  display: block;
}

.summary-tile small {
  color: rgba(255, 255, 255, 0.86);
}

.summary-tile strong {
  color: #ffffff;
  font-size: 1.18rem;
  font-weight: 700;
  margin-top: 7px;
  overflow-wrap: anywhere;
}

.legend-chip {
  color: #607d8b;
  display: inline-block;
  margin: 0 8px 10px 0;
}

.report-section {
  animation: insightCardIn 0.24s ease both;
  margin-bottom: 0;
  padding: 18px;
}

.report-panel,
.highlight-panel {
  animation: insightCardIn 0.24s ease both;
  box-shadow: none;
  margin: 0;
  padding: 24px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.report-section {
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.scheme-list-item {
  min-width: 0;
  width: 100%;
}

.scheme-card {
  min-height: 96px;
  padding-bottom: 18px;
  padding-top: 18px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.scheme-card:hover,
.report-panel:hover,
.highlight-panel:hover {
  border-color: #cfd8dc;
  box-shadow: 0 8px 22px rgba(38, 50, 56, 0.12);
  transform: translateY(-1px);
}

.scheme-row {
  align-items: center;
  display: grid;
  gap: 14px;
  min-height: 58px;
  grid-template-columns: 36px minmax(220px, 1.8fr) repeat(3, minmax(120px, 1fr)) 52px;
}

.scheme-row .material-icons {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  font-size: 21px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.measure-track {
  background: #edf2f4;
  border-radius: 999px;
  height: 9px;
  margin-top: 14px;
  overflow: hidden;
}

.measure-track span {
  background: #00838f;
  display: block;
  height: 9px;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.measure-track:hover span {
  filter: saturate(1.15);
}

.measure-track + small {
  display: block;
  margin-top: 7px;
}

.detail-heading .material-icons {
  color: #00838f;
}

.scheme-main strong {
  color: #263238;
  display: block;
}

.scheme-main small {
  color: #607d8b;
  display: block;
  margin-top: 3px;
}

.metric-block small {
  color: #607d8b;
  display: block;
}

.metric-block strong,
.metric-block h6 {
  color: #263238;
  display: block;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.metric-block .real-utilization-line,
.summary-tile small:last-child {
  color: #78909c;
  font-size: 0.76rem;
  margin-top: 4px;
}

.client-action {
  color: #00838f;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: right;
  text-transform: uppercase;
}

.detail-heading {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: 36px 1fr auto;
  margin-bottom: 18px;
  min-height: 62px;
}

.detail-heading > .material-icons {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.detail-heading h6 {
  color: #263238;
  font-weight: 700;
}

.scheme-detail-header {
  background: #ffffff;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  padding: 20px 28px 24px;
}

.scheme-overview-actions {
  border-bottom: 1px solid #e7e7e7;
  margin-bottom: 18px;
  padding-bottom: 16px;
  text-align: right;
}

.detail-actions,
.report-actions {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
}

.cancel-detail-button {
  color: #455a64;
  font-weight: 600;
  text-transform: none;
}

.action-button {
  display: flex;
  justify-content: center;
  margin: 0;
  white-space: nowrap;
}

.metric-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.metric-grid .metric-block {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  min-height: 88px;
  padding: 16px;
}

.metric-grid .metric-block:nth-child(1) {
  background: #e3f2fd;
  border-color: #bbdefb;
}

.metric-grid .metric-block:nth-child(2) {
  background: #fff3e0;
  border-color: #ffe0b2;
}

.metric-grid .metric-block:nth-child(3) {
  background: #e0f2f1;
  border-color: #b2dfdb;
}

.month-row {
  align-items: center;
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 10px;
  margin: 10px 0;
}

.month-bar {
  background: #eceff1;
  height: 8px;
}

.month-bar span {
  background: #00838f;
  animation: insightBarIn 0.38s ease both;
  display: block;
  height: 8px;
  transform-origin: left center;
  transition: width 0.28s ease, filter 0.18s ease;
}

.month-bar:hover span {
  filter: saturate(1.15);
}

.provider-row {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
}

.summary-panel {
  min-height: 86px;
}

.summary-panel h6 {
  margin-top: 0;
}

.enrollee-search {
  margin-top: 0;
}

.scheme-filter-grid {
  align-items: end;
  display: grid;
  gap: 14px;
  margin-top: 12px;
  margin-bottom: 18px;
}

.scheme-filter-grid .input-field {
  margin: 0;
}

.enrollee-filter-grid {
  grid-template-columns: minmax(260px, 0.8fr) minmax(0, 1.2fr);
}

.encounter-filter-grid {
  grid-template-columns: minmax(260px, 1.4fr) minmax(170px, 0.8fr) minmax(170px, 0.8fr) auto;
}

.encounter-actions {
  text-align: right;
}

.encounter-pager {
  align-items: center;
  border-top: 1px solid #eeeeee;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 14px;
}

.encounter-pager span {
  color: #607d8b;
  font-size: 0.88rem;
  font-weight: 600;
}

.encounter-page-button {
  color: #455a64;
  font-weight: 600;
  text-transform: none;
}

.type-chip {
  background: #eceff1;
  border-radius: 2px;
  cursor: pointer;
  display: inline-block;
  margin: 0 8px 10px 0;
  padding: 8px 12px;
}

.type-chip.active {
  background: #00838f;
  color: #ffffff;
}

@media only screen and (max-width: 1100px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .report-actions {
    grid-column: 1 / -1;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .scheme-row {
    grid-template-columns: 36px minmax(180px, 1fr) repeat(2, minmax(120px, 1fr));
  }

  .client-action {
    text-align: left;
  }
}

@media only screen and (max-width: 700px) {
  .schemes-insight-head {
    flex-direction: column;
  }

  .scope-chip {
    text-align: left;
  }

  .filter-grid,
  .summary-grid,
  .enrollee-filter-grid,
  .encounter-filter-grid {
    grid-template-columns: 1fr;
  }

  .detail-heading,
  .scheme-row {
    align-items: flex-start;
    grid-template-columns: 36px 1fr;
  }

  .detail-actions,
  .metric-block,
  .client-action {
    grid-column: 2;
  }

  .detail-actions,
  .report-actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .encounter-actions {
    text-align: left;
  }

  .encounter-pager {
    align-items: stretch;
    flex-direction: column;
  }
}

@keyframes insightCardIn {
  from {
    opacity: 0.88;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes insightBarIn {
  from {
    opacity: 0.55;
    transform: scaleX(0.35);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
</style>
